import { errorAlert } from '../helpers/alerts/sweetAlerts';
import axios from 'axios';
import {
  errorsMessage,
  PANEL_PENDING_VERIFICATION_ERROR_CODE,
} from '../helpers/Errors';
import { requestPanelVerification } from '../utils/panelVerification';

const API_NEW_BACKEND = process.env.REACT_APP_API;
const LANGUAGE = process.env.REACT_APP_LANGUAGE;

export class ApiRest {
  #axiosBase;
  // Sesión con la que salió la request (la key `token` de `localStorage` al
  // crear la instancia, aunque el `Authorization` venga por argumento).
  #sessionToken;

  constructor(
    headers = {},
    baseUrl = API_NEW_BACKEND,
    contentType = 'application/json',
    language = LANGUAGE,
    authorization = `Bearer ${window.localStorage.getItem('token')}`,
  ) {
    this.#sessionToken = window.localStorage.getItem('token');
    this.#axiosBase = axios.create({
      baseURL: baseUrl,
      headers: {
        ...headers,
        contentType,
        language,
        authorization,
      },
    });
  }

  async #executeWithRetry(requestFn, retries = 0) {
    try {
      return await requestFn();
    } catch (err) {
      if (err?.response?.status === 502 && retries < 2) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.#executeWithRetry(requestFn, retries + 1);
      }
      throw err;
    }
  }

  // `anonymous` es para endpoints sin sesión (ej. recovery-password, registro
  // v2): un 401 ahí no significa sesión vencida, así que no hace logout y lo
  // propaga.
  // `throwServerErrors` (también en `get`) lanza el 5xx como cualquier otro
  // error en vez de la alerta genérica, para quien tiene su propio estado de
  // error (ej. el 502 `1190` del token de Legal, que el modal ofrece
  // reintentar). A diferencia de `silent`, conserva el logout en 401.
  async post({ url, body, anonymous = false, throwServerErrors = false }) {
    try {
      const res = await this.#axiosBase.post(url, body);
      const { response } = res.data;
      return response || res.data;
    } catch (err) {
      return this.handleError(err?.response?.status || 1, err?.response, {
        anonymous,
        throwServerErrors,
      });
    }
  }

  async put({ url, body, anonymous = false }) {
    try {
      const res = await this.#executeWithRetry(() =>
        this.#axiosBase.put(url, body),
      );
      const { response } = res.data;
      return response;
    } catch (err) {
      return this.handleError(err?.response?.status || 1, err?.response, {
        anonymous,
      });
    }
  }

  async get({ url, params = {}, silent = false, throwServerErrors = false }) {
    try {
      const res = await this.#executeWithRetry(() =>
        this.#axiosBase.get(url, params),
      );
      const { response } = res.data;
      return response ?? res.data;
    } catch (err) {
      // `silent` evita los efectos globales de handleError (logout/redirect en
      // 401, alerta en 5xx) para requests no críticas que deben degradar sin
      // impactar la sesión del panel ni molestar al usuario.
      if (silent) {
        throw err;
      }
      return this.handleError(err?.response?.status || 1, err?.response, {
        throwServerErrors,
      });
    }
  }

  async patch({ url, params = {} }) {
    try {
      const res = await this.#executeWithRetry(() =>
        this.#axiosBase.patch(url, params),
      );
      const { response } = res.data;
      return response;
    } catch (err) {
      return this.handleError(err?.response?.status || 1, err?.response);
    }
  }

  async delete({ url, params = {} }) {
    try {
      const res = await this.#executeWithRetry(() =>
        this.#axiosBase.delete(url, params),
      );
      const { response } = res.data;
      return response;
    } catch (err) {
      return this.handleError(err?.response?.status || 1, err?.response);
    }
  }

  handleMessage(message, error_code) {
    // Un código sin mapear no debe dejar el mensaje en `undefined`.
    if (error_code && errorsMessage[error_code]) {
      return errorsMessage[error_code];
    }
    if (message) {
      return message;
    }
    return '¡Por favor valida la información e intenta nuevamente!.';
  }

  handleError(
    status,
    res,
    { anonymous = false, throwServerErrors = false } = {},
  ) {
    const response = res?.data?.response;
    const error_code = res?.data?.error_code;

    if (status === 200 || status === 201) return;

    if ((status === 401 || status === 403) && !anonymous) {
      // Una request de una sesión anterior (otro login, cambio de panel o
      // logout mientras estaba en vuelo) no puede cerrar la sesión actual.
      if (window.localStorage.getItem('token') !== this.#sessionToken) return;
      localStorage.clear();
      window.location.replace('/');
      return;
    }

    // Panel pendiente por verificación (CORP-599): la acción se bloquea con un
    // 400, no con 401/403, así que no cierra sesión. Se avisa a la UI —modal de
    // verificación si `can_verify`, aviso de contactar al administrador si no—
    // y el error se lanza igual para que la pantalla salga de su estado de
    // carga. Sus alertas de error se omiten un instante (ver
    // `isPanelVerificationPromptActive`).
    // Solo si la request es de la sesión actual: tras un cambio de panel, el
    // 1185 del panel anterior abriría el aviso en el nuevo con su `can_verify`.
    if (
      status === 400 &&
      String(error_code) === PANEL_PENDING_VERIFICATION_ERROR_CODE &&
      window.localStorage.getItem('token') === this.#sessionToken
    ) {
      requestPanelVerification({ canVerify: response?.can_verify === true });
    }

    if (status >= 500 && status <= 504 && !throwServerErrors) {
      errorAlert(
        'Error',
        '¡Disculpe ha ocurrido un error inesperado, por favor intente mas tarde!.',
      );
      return;
    }

    throw {
      status,
      message: this.handleMessage(response?.message, error_code),
      info: response?.info,
      error_code,
    };
  }
}
